import { LoadingOutlined } from '@ant-design/icons';
import { ProFormSelect, ProFormSwitch } from '@ant-design/pro-form';
import { useHover } from 'ahooks';
import { Checkbox, Tooltip } from 'antd';
import { FormInstance } from 'antd/es/form/Form';
import classnames from 'classnames';
import { isArray } from 'lodash';
import { observer } from 'mobx-react-lite';
import React, { FC, useEffect, useRef, useState } from 'react';
import {
  SelectFormTypeField,
  SelectFormTypeOption,
} from '@/presentation/components/form';
import {
  getValueFromEvent,
  getValueFromEventOption,
} from '@/presentation/components/form/utils';
import { TdsFormLabel } from '@/presentation/components/tds-form/tds-form-label';
import { useStores } from '@/presentation/store';
import { ExOption, FIELD_ALL, FieldKey, FieldScope, getLocalField, Option } from '@/proto/field';
import styles from './select.module.scss';

const modeType = ['multiple', 'tags'];

export const SelectForm: FC<{
  entry: SelectFormTypeField | SelectFormTypeOption,
  hidden?: boolean,
  form: FormInstance,
  onChange?: (value: Record<string, any>, fields: Record<string, any>) => void;
}> = observer((props) => {
  const { entry, hidden, form } = props;
  const { name, label, mode, initialValue, placeholder, dropdownMatchSelectWidth,
    filterOption, longSearch = true, enableExcludeMode, enableAutoSearch, labelString, labelTools } = entry;
  const [isFocus, setIsFocus] = useState(false);
  const [isHover, setIsHover] = useState(false);
  const [dropdownVisible, setDropdownVisible] = useState(false);
  const [value, setValue] = useState<Option[] | ExOption[]>([]);
  const [optionLen, setOptionLen] = useState(0);// 选中数量
  const [searchValue, setSearchValue] = useState<string>('');
  const fieldKey = 'fieldKey' in entry ? (entry as SelectFormTypeField).fieldKey : undefined;
  const fieldScope = 'fieldScope' in entry ? (entry as SelectFormTypeField).fieldScope : undefined;
  const userOptions = 'options' in entry ? (entry as SelectFormTypeOption).options : undefined;
  const [options, setOptions] = useState<Option[] | ExOption[]>(() => (userOptions ? userOptions : [{ value: FIELD_ALL, label: '所有' }]));
  const { loadField } = useStores().FieldStore;
  const [loaded, setLoaded] = useState(false);
  const [loading, setLoading] = useState(false);
  const [hasValueChanged, setHasValueChanged] = useState(false);// 标志选中情况是否有改变
  const [switchChecked, setSwitchChecked] = useState<boolean>();// 控制排除按钮是否打开
  const [switchDisabled, setSwitchDisabled] = useState<boolean>(true); // 控制排除按钮是否可用，选择所有时直接不可用
  const domRef = useRef(null);
  const isHovering = useHover(domRef);

  const onChange = (_args: string[], option: any) => {
    const value = getValueFromEventOption(option as Option[] | ExOption[], mode);
    setValue(value);
    if (enableExcludeMode || enableAutoSearch) {
      setHasValueChanged(true);
      if (!dropdownVisible) {
        props?.onChange?.(form.getFieldValue(entry.name), form.getFieldsValue());// 用户不点开下拉框直接删除选项，需要触发请求
      }
    }
  };

  const renderTips = () => <>{Array.isArray(value) ? value.map(item => <div>{item.label}</div>) : null}</>;
  const tipsVisible = isHover && !isFocus && !dropdownVisible && Array.isArray(value) && value.length > 1;

  const onSearch = (value: string) => {
    setSearchValue(value);
  };


  // 一次输入多个数据后选中
  const onInputKeyDown = (e: any) => {
    if (e.key === 'Enter' && modeType.includes(mode) && longSearch) {
      const search = e?.target?.value?.split?.(/;|,/) || [];
      if (search.length > 0) {
        e.stopPropagation();
        e.preventDefault();
        let searchValues = search;
        // mode=multiple需要判断输入值是否存在options中
        if (mode === modeType[0]) {
          searchValues = options?.filter(item => search.includes(item.label));
        }
        const newValue = getValueFromEventOption(searchValues as Option[] | ExOption[], mode);
        setValue(value => ([...value, ...newValue]));
        form.setFieldsValue({ [name]: [...value, ...newValue?.map(item => item.value || item)] });
        setSearchValue('');
      }
    }
  };

  // 输入多个数据后筛选
  const defaultFilterOption = (inputValue: string, option: any) => {
    const valueList = inputValue?.split?.(/;|,/);
    if (valueList?.length > 1) {
      return valueList?.includes?.(option.label);
    }
    return option?.label?.toLowerCase?.()?.indexOf?.(inputValue?.toLowerCase?.() || '') >= 0;
  };

  /**
   * 展开时加载
   * @param open
   */
  const onDropdownVisibleChange = async (open: boolean) => {
    setDropdownVisible(open);

    const needRemote = (fieldKey && fieldScope && !userOptions);
    if (open && needRemote && !loaded) {
      let field = getLocalField(fieldKey as FieldKey);
      if (field) {
        setOptions(field);
      } else {
        setLoading(true);
        field = await loadField(fieldKey as FieldKey, fieldScope as FieldScope);
        setOptions(field);
        setLoading(false);
      }
      setLoaded(true);
    }
  };

  const initOptions = () => {
    if (userOptions) {
      setOptions(userOptions);
      return;
    }

    const field = getLocalField(fieldKey as FieldKey);
    setOptions(field || [{ value: FIELD_ALL, label: '所有' }]);
  };
  // 排除开关状态改变肯定导致所选数据改变
  const onSwitchChange = (_args: boolean) => {
    setSwitchChecked(_args);
    setHasValueChanged(true);
  };

  const optionItemRender = (props: any) =>  {
    let checkedState;
    if (isArray(form.getFieldValue(name))) {
      checkedState = form.getFieldValue(name)?.some((val: any) => val === props.value);
      // 刷新后首次进入页面未做任何选择时，value无法及时同步token解析出的选择情况,因此统一用form.getFieldValue(name）
    } else {
      checkedState = form.getFieldValue(name) === props.value;// 聚类堆栈value不是数组，特殊处理
    }
    return (
      <div className={styles.optionItemRender}>
        <Checkbox
          className={styles.optionCheckRender}
          checked={checkedState}
        />
        <span className={styles.optionLabelRender}>{props.label}</span>
      </div>
    );
  };

  const dropdownRender = (originNode: any) => {
    if (loading) {
      return <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24 }}><LoadingOutlined /></div>;
    }
    if (enableExcludeMode) {
      return (
        <div className={styles.select}>
          {originNode}
          <div
            className={styles.footer}
          >
            <ProFormSwitch
              initialValue={false}
              name={`${name}OperatorConvert`}
              label={<span style={{ paddingLeft: '12px' }}>排除</span>}
              disabled={switchDisabled}
              fieldProps={{ onChange: onSwitchChange }}
            />
            <span className={styles.footerTag}>
              已
              {switchChecked || form.getFieldValue(`${name}OperatorConvert`)
                ? '排除:'
                : '包含:'}
              {optionLen}项
            </span>
          </div>
        </div>
      );
    }
    return originNode;
  };

  useEffect(() => {
    initOptions();
  }, [userOptions]);

  useEffect(() => {
    // 不改造为支持排除的，直接返回不做处理
    if (!enableExcludeMode) {
      return;
    }
    // 处理排除按钮的逻辑
    if (
      (isArray(form.getFieldValue(name))
        && (form.getFieldValue(name)?.length === 0 || form.getFieldValue(name)?.some((val: string) => val === '')))  // 刷新后首次进入页面未做任何选择时，value无法及时同步token解析出的选择情况,因此统一用form.getFieldValue(name）
      || (form.getFieldValue(name) !== 0 && !form.getFieldValue(name))// 聚类堆栈的value不是数组类型,不选择任何选项，为undefined。
    ) {
      form.setFieldsValue({ [`${name}OperatorConvert`]: false });
      setSwitchChecked(false);
      setSwitchDisabled(true);
    } else {
      setSwitchDisabled(false);
    }

    // 处理选中数据数量的逻辑
    if (isArray(form.getFieldValue(name))) { // 刷新后首次进入页面未做任何选择时，value无法及时同步token解析出的选中数量,因此统一用form.getFieldValue(name）
      setOptionLen(form.getFieldValue(name)?.length);
    } else if (form.getFieldValue(name) !== 0 && !form.getFieldValue(name)) {
      // 聚类堆栈的value不是数组类型。不选择任何选项时，form.getFieldValue(name)是undefine。选中第一项时是0，需要排除掉
      setOptionLen(0);
    } else {
      setOptionLen(1);
    }
  }, [value, form.getFieldValue(name)]);

  useEffect(() => {
    if (!dropdownVisible && hasValueChanged && (enableExcludeMode || enableAutoSearch)) {
      props?.onChange?.(form.getFieldValue(entry.name), form.getFieldsValue());
      setHasValueChanged(false);
    }
  }, [dropdownVisible]);// 用户点开下拉框选择/删除选项且下拉框收起后触发。不用onblur是因为下拉框未收起时点击某些区域会导致页面失焦。


  return (
    <div ref={domRef} className={
      enableExcludeMode
        ? classnames({
          [styles.tdsLabel]: true,
          [styles.commentRenderNot]: form.getFieldValue(`${name}OperatorConvert`),
          [styles.commentRenderIncludeForCluster]: entry.name === FieldKey.ClusterStackType && !form.getFieldValue(`${name}OperatorConvert`),
          [styles.commentRenderNotForCluster]: entry.name === FieldKey.ClusterStackType && form.getFieldValue(`${name}OperatorConvert`),
        })
        : styles.tdsLabel
    }>
    <Tooltip title={renderTips()} visible={tipsVisible} >
      <ProFormSelect
        name={name}
        label={<TdsFormLabel isHovering={isHovering}
                             label={labelString || label}
                             tools={labelTools}/>}
        mode={mode}
        hidden={hidden}
        showSearch
        initialValue={initialValue}
        placeholder={placeholder || '所有'}
        options={options.map(item => ({
          ...item,
          ['data-tid']: `${name}-${item.label}`,
        }))}
        getValueFromEvent={(args: string[]) => getValueFromEvent(args, mode)}
        transform={entry.transform}
        convertValue={entry.convertValue}
        fieldProps={{
          maxTagCount: 'responsive',
          onDropdownVisibleChange,
          dropdownMatchSelectWidth,
          dropdownRender,
          onChange,
          onFocus: () => setIsFocus(true),
          onBlur: () => setIsFocus(false),
          onMouseEnter: () => setIsHover(true),
          onMouseLeave: () => setIsHover(false),
          dropdownClassName: classnames({
            [styles.dropDown]: true,
            [styles.dropDownBoxExclude]: form.getFieldValue(`${name}OperatorConvert`),
          }),
          filterOption: filterOption ?? defaultFilterOption,
          onSearch,
          autoClearSearchValue: true,
          searchValue,
          onInputKeyDown,
          tokenSeparators: [';', ','],
          optionItemRender: enableExcludeMode ? optionItemRender : undefined,
          ...(entry.fieldProps || {}),
        }}
      />
    </Tooltip>
    </div>
  );
});
